/* ============================================================
   Dathent — Admin Users (/app/admin/users)
   Gated server-side by requireAdmin() (auth-worker owns admin-users.ts /
   admin-user-detail.ts — TASK-108/109, EPIC-017 #196). Paginated user list
   with filters/sort + a click-through detail card that aggregates profile,
   attribution (signup source/UTM), organization+billing, subscription,
   connected social accounts, usage, payments and autopilot state in one
   request.

   Security is 100% server-side (auth-worker requireAdmin); this screen
   carries no client guard, mirroring AdminActivation / AdminPlans /
   AdminSupport. Filters are Apply-gated (a local draft, only sent to the
   server on "Apply" or "Reset") so typing in a date/text field never fires a
   request per keystroke. Every read uses cacheTTL:0 so the list/stats/detail
   always reflect the live D1 state — this is an ops tool, not a cached view.

   Every block in /detail is independently fail-soft server-side: absence of
   data (no subscription, no social accounts) is normal, but a query that
   actually failed lands in `warnings[]` and is surfaced here as an amber
   note rather than silently hidden.
   ============================================================ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, DathentAPIError } from '../services/api/client.js';
import {
  CREDIT_ACCOUNTS,
  CREDIT_KINDS,
  buildHistoryParams,
  formatCredits,
  kindDirection,
  validateGrant,
  validateRevoke,
  validateReason,
} from '../lib/adminCredits.js';

const DEFAULT_LIMIT = 50;

const ACCOUNT_STATUSES = ['active', 'suspended', 'pending'];
const SUBSCRIPTION_STATUSES = ['active', 'trialing', 'canceled', 'none'];
const SORT_FIELDS = [
  { value: 'created_at', label: 'Created' },
  { value: 'email', label: 'Email' },
  { value: 'plan', label: 'Plan' },
  { value: 'billing_status', label: 'Billing status' },
  { value: 'current_period_end', label: 'Period end' },
];

const DEFAULT_DRAFT = {
  q: '',
  status: 'all',
  subscription: 'all',
  trial: 'all',
  plan: '',
  createdFrom: '',
  createdTo: '',
  periodEndFrom: '',
  periodEndTo: '',
  source: '',
  sort: 'created_at',
  order: 'desc',
};

const WARNING_LABELS = {
  organization: 'Organization & billing',
  subscription: 'Subscription',
  socialAccounts: 'Social accounts',
  usage: 'Usage',
  payments: 'Payments',
  autopilot: 'Autopilot',
  byPlan: 'By plan',
  bySubscription: 'By subscription',
  bySource: 'By source',
  conversion: 'Conversion',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtAmount(amount, currency) {
  if (amount == null) return '—';
  const cur = (currency || 'usd').toUpperCase();
  return `${(amount / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${cur}`;
}

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

// ─── style primitives (mirrors AdminActivation.jsx / AdminPlans.jsx / AdminSupport.jsx) ───

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '10px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '9px 11px', fontSize: 13.5, fontFamily: 'inherit',
};
const select = { ...input, padding: '8px 10px' };
const btn = (disabled) => ({
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnGhost = (disabled) => ({
  background: 'transparent', color: 'var(--text, #ececf0)', border: '1px solid var(--border, #2a2a30)',
  borderRadius: 10, padding: '9px 16px', fontSize: 13.5, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
  border: '1px solid var(--border, #2a2a30)', borderRadius: 999, padding: '3px 10px',
  color: 'var(--text-muted, #9a9aa2)',
};

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

function AmberNote({ children }) {
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 12.5,
      background: '#caa24a18', border: '1px solid #caa24a55', color: '#e7c878', lineHeight: 1.5,
    }}>{children}</div>
  );
}

function warningsList(warnings) {
  if (!warnings || warnings.length === 0) return null;
  return warnings.map((w) => WARNING_LABELS[w] || w).join(', ');
}

const SUBSCRIPTION_COLOR = {
  active: '#7ee2a8',
  trialing: '#e7c878',
  canceled: '#e8a59f',
  none: '#9a9aa2',
};

function SubscriptionPill({ status }) {
  if (!status) return <span style={{ color: 'var(--text-muted, #9a9aa2)' }}>—</span>;
  const color = SUBSCRIPTION_COLOR[status] || '#9a9aa2';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color,
      border: `1px solid ${color}55`, borderRadius: 999, padding: '2px 9px',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} />
      {status}
    </span>
  );
}

const ACCOUNT_STATUS_COLOR = { active: '#7ee2a8', suspended: '#e8a59f', pending: '#e7c878' };

function AccountStatusDot({ status }) {
  const color = ACCOUNT_STATUS_COLOR[status] || '#9a9aa2';
  return <span title={status} style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: color, marginRight: 8, flexShrink: 0 }} />;
}

// ─── stats strip ────────────────────────────────────────────────────────────

function StatsStrip({ stats, loading, error }) {
  if (loading) return <div style={{ ...card, marginBottom: 16 }}>Loading stats…</div>;
  if (error) return <div style={{ ...card, marginBottom: 16 }}><ErrorNote error={error} /></div>;
  if (!stats) return null;

  const tiles = [
    { l: 'Total users', v: stats.total },
    { l: 'Active', v: stats.active },
    { l: 'New (7d)', v: stats.newLast7Days },
    { l: 'Conversion', v: stats.conversion ? `${fmtPct(stats.conversion.rate)} (${stats.conversion.paid}/${stats.conversion.signups})` : '—' },
  ];

  const byPlanEntries = stats.byPlan
    ? Object.entries(stats.byPlan).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          {tiles.map((t) => (
            <div key={t.l}>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t.l}</div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}>{t.v ?? '—'}</div>
            </div>
          ))}
        </div>
        {stats.warnings && stats.warnings.length > 0 && (
          <span style={{ ...chip, color: '#e7c878', borderColor: '#caa24a55' }} title={warningsList(stats.warnings)}>
            ⚠ stats partial: {warningsList(stats.warnings)}
          </span>
        )}
      </div>

      {byPlanEntries.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>By plan</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {byPlanEntries.map(([plan, n]) => (
              <span key={plan} style={chip}>{plan}: <strong style={{ color: 'var(--text, #ececf0)', marginLeft: 4 }}>{n}</strong></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── filters panel ──────────────────────────────────────────────────────────

function FiltersPanel({ draft, setDraft, planOptions, onApply, onReset, disabled }) {
  const setF = (k, v) => setDraft((cur) => ({ ...cur, [k]: v }));

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Search (email / name)</label>
          <input style={input} value={draft.q} onChange={(e) => setF('q', e.target.value)} placeholder="user@example.com" disabled={disabled} />
        </div>
        <div>
          <label style={label}>Account status</label>
          <select style={select} value={draft.status} onChange={(e) => setF('status', e.target.value)} disabled={disabled}>
            <option value="all">All</option>
            {ACCOUNT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Subscription</label>
          <select style={select} value={draft.subscription} onChange={(e) => setF('subscription', e.target.value)} disabled={disabled}>
            <option value="all">All</option>
            {SUBSCRIPTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Trial</label>
          <select style={select} value={draft.trial} onChange={(e) => setF('trial', e.target.value)} disabled={disabled}>
            <option value="all">All</option>
            <option value="1">In trial</option>
            <option value="0">Not in trial</option>
          </select>
        </div>
        <div>
          <label style={label}>Plan</label>
          <input
            style={input}
            list="admin-users-plan-options"
            value={draft.plan}
            onChange={(e) => setF('plan', e.target.value)}
            placeholder="e.g. pro"
            disabled={disabled}
          />
          <datalist id="admin-users-plan-options">
            {planOptions.map((p) => <option key={p} value={p} />)}
          </datalist>
        </div>
        <div>
          <label style={label}>Source</label>
          <input style={input} value={draft.source} onChange={(e) => setF('source', e.target.value)} placeholder="referrer or utm_source" disabled={disabled} />
        </div>
        <div>
          <label style={label}>Created from</label>
          <input type="date" style={input} value={draft.createdFrom} onChange={(e) => setF('createdFrom', e.target.value)} disabled={disabled} />
        </div>
        <div>
          <label style={label}>Created to</label>
          <input type="date" style={input} value={draft.createdTo} onChange={(e) => setF('createdTo', e.target.value)} disabled={disabled} />
        </div>
        <div>
          <label style={label}>Period end from</label>
          <input type="date" style={input} value={draft.periodEndFrom} onChange={(e) => setF('periodEndFrom', e.target.value)} disabled={disabled} />
        </div>
        <div>
          <label style={label}>Period end to</label>
          <input type="date" style={input} value={draft.periodEndTo} onChange={(e) => setF('periodEndTo', e.target.value)} disabled={disabled} />
        </div>
        <div>
          <label style={label}>Sort by</label>
          <select style={select} value={draft.sort} onChange={(e) => setF('sort', e.target.value)} disabled={disabled}>
            {SORT_FIELDS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Order</label>
          <select style={select} value={draft.order} onChange={(e) => setF('order', e.target.value)} disabled={disabled}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <button type="button" style={btn(disabled)} disabled={disabled} onClick={onApply}>Apply</button>
        <button type="button" style={btnGhost(disabled)} disabled={disabled} onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

// ─── users table ────────────────────────────────────────────────────────────

function UsersTable({ users, loading, error, onSelect }) {
  if (loading) return <div style={card}>Loading users…</div>;
  if (error) return <div style={card}><ErrorNote error={error} /></div>;
  if (users.length === 0) return <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)' }}>No users match these filters.</div>;

  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 10px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '10px 10px', fontSize: 13, borderTop: '1px solid var(--border, #2a2a30)', whiteSpace: 'nowrap' };

  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, paddingLeft: 20 }}>Email</th>
            <th style={th}>Name</th>
            <th style={th}>Plan</th>
            <th style={th}>Subscription</th>
            <th style={th}>Trial ends</th>
            <th style={th}>Period ends</th>
            <th style={th}>Source</th>
            <th style={{ ...th, paddingRight: 20 }}>Created</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              onClick={() => onSelect(u.id)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2, #0f0f12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <td style={{ ...td, paddingLeft: 20 }}>
                <AccountStatusDot status={u.status} />
                {u.email}
                {u.isAdmin && <span style={{ ...chip, marginLeft: 8, fontSize: 10.5 }}>admin</span>}
              </td>
              <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{u.name || '—'}</td>
              <td style={td}>{u.plan || '—'}</td>
              <td style={td}><SubscriptionPill status={u.subscriptionStatus} /></td>
              <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{fmtDate(u.trialEnd)}</td>
              <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{fmtDate(u.currentPeriodEnd)}</td>
              <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{u.signupSource || '—'}</td>
              <td style={{ ...td, paddingRight: 20, color: 'var(--text-muted, #9a9aa2)' }}>{fmtDate(u.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── detail modal ───────────────────────────────────────────────────────────

function DetailRow({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0', borderTop: '1px solid var(--border, #2a2a30)' }}>
      <span style={{ color: 'var(--text-muted, #9a9aa2)' }}>{k}</span>
      <span style={{ textAlign: 'right' }}>{v}</span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div style={{
      background: 'var(--surface-2, #0f0f12)', border: '1px solid var(--border, #2a2a30)',
      borderRadius: 10, padding: 14, marginTop: 14,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

// ─── credits tab (TASK-234, issue #352; EPIC-021 #227) ─────────────────────
// Talks to api.adminCredits (client.js) → gateway's /api/admin/credits/* →
// stripe-worker's /internal/credits/admin/*. Every read is cacheTTL:0 — this
// is an ops tool over the live ledger, never a cached view. Validation lives
// in ../lib/adminCredits.js (pure, unit-tested); this component only wires
// it to the API and to i18n via the `adminCredits.*` block.

const CREDIT_LEDGER_DEFAULT_LIMIT = 25;

const CREDITS_ACTION_FIELDS = {
  grant: ['account', 'amount', 'reason'],
  revoke: ['account', 'amount', 'reason', 'grantKey'],
  freeze: ['reason'],
  unfreeze: ['reason'],
  resolveFraud: ['reason'],
};

function CreditsActionModal({ type, organizationId, mainCents, onClose, onDone, t }) {
  const [form, setForm] = useState({ account: 'bonus', amount: '', reason: '', grantKey: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const setF = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));
  const fields = CREDITS_ACTION_FIELDS[type] || [];

  const titleKey = {
    grant: 'modal.grantTitle', revoke: 'modal.revokeTitle', freeze: 'modal.freezeTitle',
    unfreeze: 'modal.unfreezeTitle', resolveFraud: 'modal.resolveTitle',
  }[type];
  const titleDefault = {
    grant: 'Grant credits', revoke: 'Revoke credits', freeze: 'Freeze account',
    unfreeze: 'Unfreeze account', resolveFraud: 'Resolve fraud flag',
  }[type];

  const submit = async () => {
    setApiError(null);
    let validated = null;
    if (type === 'grant' || type === 'revoke') {
      const result = type === 'grant'
        ? validateGrant({ organizationId, ...form })
        : validateRevoke({ organizationId, ...form }, { mainCents });
      if (!result.ok) { setErrors(result.errors); return; }
      validated = result.value;
    } else {
      const reason = validateReason(form.reason);
      if (!reason) { setErrors({ reason: 'reasonRequired' }); return; }
      validated = { reason };
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (type === 'grant') await api.adminCredits.grant(validated);
      else if (type === 'revoke') await api.adminCredits.revoke(validated);
      else if (type === 'freeze') await api.adminCredits.freeze(organizationId, validated.reason);
      else if (type === 'unfreeze') await api.adminCredits.unfreeze(organizationId, validated.reason);
      else if (type === 'resolveFraud') return onDone(validated.reason);
      onDone();
    } catch (e) {
      setApiError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 20px', overflowY: 'auto' }}
      onClick={onClose}
    >
      <div style={{ ...card, width: '100%', maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t(`adminCredits.${titleKey}`, titleDefault)}</h3>

        {fields.includes('account') && (
          <>
            <label style={label}>{t('adminCredits.form.account', 'Account')}</label>
            <select style={select} value={form.account} onChange={(e) => setF('account', e.target.value)} disabled={submitting}>
              {CREDIT_ACCOUNTS.map((a) => <option key={a} value={a}>{t(`adminCredits.balance.${a}`, a)}</option>)}
            </select>
            {errors.account && <div style={{ color: '#e8a59f', fontSize: 12, marginTop: 4 }}>{t(`adminCredits.errors.${errors.account}`, errors.account)}</div>}
          </>
        )}

        {fields.includes('amount') && (
          <>
            <label style={label}>{t('adminCredits.form.amount', 'Amount (credits)')}</label>
            <input style={input} value={form.amount} onChange={(e) => setF('amount', e.target.value)} placeholder="10.00" disabled={submitting} />
            {errors.amount && <div style={{ color: '#e8a59f', fontSize: 12, marginTop: 4 }}>{t(`adminCredits.errors.${errors.amount}`, errors.amount)}</div>}
          </>
        )}

        {fields.includes('grantKey') && (
          <>
            <label style={label}>{t('adminCredits.form.grantKey', 'Grant key (optional, bonus only)')}</label>
            <input style={input} value={form.grantKey} onChange={(e) => setF('grantKey', e.target.value)} placeholder={t('adminCredits.form.grantKeyPlaceholder', 'e.g. topup:pi_1:bonus')} disabled={submitting || form.account !== 'bonus'} />
            {errors.grantKey && <div style={{ color: '#e8a59f', fontSize: 12, marginTop: 4 }}>{t(`adminCredits.errors.${errors.grantKey}`, errors.grantKey)}</div>}
          </>
        )}

        {fields.includes('reason') && (
          <>
            <label style={label}>{t('adminCredits.form.reason', 'Reason')}</label>
            <input style={input} value={form.reason} onChange={(e) => setF('reason', e.target.value)} placeholder={t('adminCredits.form.reasonPlaceholder', 'Why are you making this change?')} disabled={submitting} />
            {errors.reason && <div style={{ color: '#e8a59f', fontSize: 12, marginTop: 4 }}>{t(`adminCredits.errors.${errors.reason}`, errors.reason)}</div>}
          </>
        )}

        <ErrorNote error={apiError} />

        <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" style={btnGhost(submitting)} disabled={submitting} onClick={onClose}>
            {t('adminCredits.actions.cancel', 'Cancel')}
          </button>
          <button type="button" style={btn(submitting)} disabled={submitting} onClick={submit}>
            {submitting ? t('adminCredits.actions.submitting', 'Submitting…') : t('adminCredits.actions.submit', 'Submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

const CREDIT_DIRECTION_COLOR = { credit: '#7ee2a8', debit: '#e8a59f', unknown: '#9a9aa2' };

function CreditsTab({ organization }) {
  const { t } = useTranslation();
  const organizationId = organization?.id || null;

  const [balance, setBalance] = useState({ loading: true, error: null, data: null });
  const [ledgerDraft, setLedgerDraft] = useState({ account: 'all', kind: 'all', from: '', to: '' });
  const [ledgerApplied, setLedgerApplied] = useState({ account: 'all', kind: 'all', from: '', to: '' });
  const [ledgerOffset, setLedgerOffset] = useState(0);
  const [ledger, setLedger] = useState({ loading: true, error: null, entries: [], total: 0, limit: CREDIT_LEDGER_DEFAULT_LIMIT });
  const [grants, setGrants] = useState({ loading: true, error: null, entries: [] });
  const [fraudUnresolvedOnly, setFraudUnresolvedOnly] = useState(true);
  const [fraud, setFraud] = useState({ loading: true, error: null, entries: [] });
  const [actionModal, setActionModal] = useState(null); // 'grant' | 'revoke' | 'freeze' | 'unfreeze' | { type: 'resolveFraud', flagId }
  const [flagError, setFlagError] = useState(null);

  const loadBalance = useCallback(async () => {
    if (!organizationId) return;
    setBalance((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminCredits.balance(organizationId);
      setBalance({ loading: false, error: null, data: res?.balance ?? res ?? null });
    } catch (e) {
      setBalance({ loading: false, error: errMsg(e), data: null });
    }
  }, [organizationId]);

  const loadLedger = useCallback(async () => {
    if (!organizationId) return;
    setLedger((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const params = buildHistoryParams({ organizationId, ...ledgerApplied, limit: CREDIT_LEDGER_DEFAULT_LIMIT, offset: ledgerOffset });
      const res = await api.adminCredits.history(params);
      setLedger({
        loading: false, error: null,
        entries: res?.entries || res?.history || [], total: res?.total ?? 0, limit: res?.limit ?? CREDIT_LEDGER_DEFAULT_LIMIT,
      });
    } catch (e) {
      setLedger((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [organizationId, ledgerApplied, ledgerOffset]);

  const loadGrants = useCallback(async () => {
    if (!organizationId) return;
    setGrants((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminCredits.grants(organizationId);
      setGrants({ loading: false, error: null, entries: res?.grants || [] });
    } catch (e) {
      setGrants({ loading: false, error: errMsg(e), entries: [] });
    }
  }, [organizationId]);

  const loadFraud = useCallback(async () => {
    if (!organizationId) return;
    setFraud((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminCredits.fraudFlags({ organizationId, unresolved: fraudUnresolvedOnly });
      setFraud({ loading: false, error: null, entries: res?.flags || [] });
    } catch (e) {
      setFraud({ loading: false, error: errMsg(e), entries: [] });
    }
  }, [organizationId, fraudUnresolvedOnly]);

  useEffect(() => { loadBalance(); }, [loadBalance]);
  useEffect(() => { loadLedger(); }, [loadLedger]);
  useEffect(() => { loadGrants(); }, [loadGrants]);
  useEffect(() => { loadFraud(); }, [loadFraud]);

  const refreshAll = () => { loadBalance(); loadLedger(); loadGrants(); loadFraud(); };

  const onLedgerApply = () => { setLedgerOffset(0); setLedgerApplied(ledgerDraft); };
  const onLedgerReset = () => {
    const reset = { account: 'all', kind: 'all', from: '', to: '' };
    setLedgerDraft(reset); setLedgerApplied(reset); setLedgerOffset(0);
  };

  if (!organizationId) {
    return <AmberNote>{t('adminCredits.noOrganization', 'No organization on this account — credits are managed per organization.')}</AmberNote>;
  }

  const b = balance.data;
  const isFrozen = Boolean(b?.frozen);
  const total = ledger.total;
  const limit = ledger.limit || CREDIT_LEDGER_DEFAULT_LIMIT;
  const rangeStart = total === 0 ? 0 : ledgerOffset + 1;
  const rangeEnd = Math.min(ledgerOffset + limit, total);

  return (
    <div>
      {/* Balance */}
      <DetailSection title={t('adminCredits.balance.title', 'Balance')}>
        {balance.loading ? (
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.balance.loading', 'Loading balance…')}</p>
        ) : balance.error ? (
          <ErrorNote error={balance.error} />
        ) : (
          <>
            <DetailRow k={t('adminCredits.balance.main', 'Main')} v={formatCredits(b?.mainCents)} />
            <DetailRow k={t('adminCredits.balance.bonus', 'Bonus')} v={formatCredits(b?.bonusCents)} />
            <DetailRow k={t('adminCredits.balance.total', 'Total')} v={formatCredits(b?.totalCents)} />
            <DetailRow
              k={t('adminCredits.balance.status', 'Status')}
              v={(
                <span style={{ color: isFrozen ? '#e8a59f' : '#7ee2a8' }}>
                  {isFrozen ? t('adminCredits.balance.frozen', 'Frozen') : t('adminCredits.balance.active', 'Active')}
                </span>
              )}
            />
          </>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btn(false)} onClick={() => setActionModal('grant')}>{t('adminCredits.actions.grant', 'Grant')}</button>
          <button type="button" style={btnGhost(false)} onClick={() => setActionModal('revoke')}>{t('adminCredits.actions.revoke', 'Revoke')}</button>
          {isFrozen ? (
            <button type="button" style={btnGhost(false)} onClick={() => setActionModal('unfreeze')}>{t('adminCredits.actions.unfreeze', 'Unfreeze')}</button>
          ) : (
            <button type="button" style={btnGhost(false)} onClick={() => setActionModal('freeze')}>{t('adminCredits.actions.freeze', 'Freeze')}</button>
          )}
        </div>
      </DetailSection>

      {/* Ledger */}
      <DetailSection title={t('adminCredits.ledger.title', 'Ledger')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginTop: 6 }}>
          <div>
            <label style={label}>{t('adminCredits.ledger.filters.account', 'Account')}</label>
            <select style={select} value={ledgerDraft.account} onChange={(e) => setLedgerDraft((c) => ({ ...c, account: e.target.value }))}>
              <option value="all">{t('adminCredits.ledger.filters.all', 'All')}</option>
              {CREDIT_ACCOUNTS.map((a) => <option key={a} value={a}>{t(`adminCredits.balance.${a}`, a)}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>{t('adminCredits.ledger.filters.kind', 'Kind')}</label>
            <select style={select} value={ledgerDraft.kind} onChange={(e) => setLedgerDraft((c) => ({ ...c, kind: e.target.value }))}>
              <option value="all">{t('adminCredits.ledger.filters.all', 'All')}</option>
              {CREDIT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>{t('adminCredits.ledger.filters.from', 'From')}</label>
            <input type="date" style={input} value={ledgerDraft.from} onChange={(e) => setLedgerDraft((c) => ({ ...c, from: e.target.value }))} />
          </div>
          <div>
            <label style={label}>{t('adminCredits.ledger.filters.to', 'To')}</label>
            <input type="date" style={input} value={ledgerDraft.to} onChange={(e) => setLedgerDraft((c) => ({ ...c, to: e.target.value }))} />
          </div>
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <button type="button" style={btn(ledger.loading)} disabled={ledger.loading} onClick={onLedgerApply}>{t('adminCredits.ledger.filters.apply', 'Apply')}</button>
          <button type="button" style={btnGhost(ledger.loading)} disabled={ledger.loading} onClick={onLedgerReset}>{t('adminCredits.ledger.filters.reset', 'Reset')}</button>
        </div>

        {ledger.loading ? (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.ledger.loading', 'Loading ledger…')}</p>
        ) : ledger.error ? (
          <ErrorNote error={ledger.error} />
        ) : ledger.entries.length === 0 ? (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.ledger.empty', 'No ledger entries match these filters.')}</p>
        ) : (
          <div style={{ marginTop: 10, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  {['date', 'kind', 'account', 'amount', 'reason', 'admin'].map((c) => (
                    <th key={c} style={{ textAlign: 'left', color: 'var(--text-muted, #9a9aa2)', fontWeight: 500, padding: '4px 8px 6px 0', whiteSpace: 'nowrap' }}>
                      {t(`adminCredits.ledger.columns.${c}`, c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e, i) => {
                  const dir = kindDirection(e.kind);
                  return (
                    <tr key={e.id ?? i} style={{ borderTop: '1px solid var(--border, #2a2a30)' }}>
                      <td style={{ padding: '6px 8px 6px 0', whiteSpace: 'nowrap' }}>{fmtDate(e.createdAt)}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{e.kind}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{t(`adminCredits.balance.${e.account}`, e.account)}</td>
                      <td style={{ padding: '6px 8px 6px 0', color: CREDIT_DIRECTION_COLOR[dir] }}>{formatCredits(e.amountCents, { signed: true })}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{e.reason || '—'}</td>
                      <td style={{ padding: '6px 8px 6px 0', color: 'var(--text-muted, #9a9aa2)' }}>{e.adminUserId || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>
            <span>{t('adminCredits.ledger.showing', 'Showing {{start}}–{{end}} of {{total}}', { start: rangeStart, end: rangeEnd, total })}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={btnGhost(ledgerOffset === 0 || ledger.loading)} disabled={ledgerOffset === 0 || ledger.loading} onClick={() => setLedgerOffset((o) => Math.max(0, o - limit))}>
                {t('adminCredits.ledger.prev', 'Prev')}
              </button>
              <button type="button" style={btnGhost(ledgerOffset + limit >= total || ledger.loading)} disabled={ledgerOffset + limit >= total || ledger.loading} onClick={() => setLedgerOffset((o) => o + limit)}>
                {t('adminCredits.ledger.next', 'Next')}
              </button>
            </div>
          </div>
        )}
      </DetailSection>

      {/* Grants */}
      <DetailSection title={t('adminCredits.grants.title', 'Grants')}>
        {grants.loading ? (
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.grants.loading', 'Loading grants…')}</p>
        ) : grants.error ? (
          <ErrorNote error={grants.error} />
        ) : grants.entries.length === 0 ? (
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.grants.empty', 'No grants on record.')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {grants.entries.map((g, i) => (
              <div key={g.key ?? i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}>
                <span><code style={{ fontSize: 11 }}>{g.key}</code></span>
                <span style={{ display: 'flex', gap: 10, color: 'var(--text-muted, #9a9aa2)' }}>
                  <span>{formatCredits(g.remainingCents)} / {formatCredits(g.amountCents)}</span>
                  <span style={chip}>{g.status}</span>
                  <span>{fmtDate(g.expiresAt)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </DetailSection>

      {/* Fraud flags */}
      <DetailSection title={t('adminCredits.fraud.title', 'Fraud flags')}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 4 }}>
          <input type="checkbox" checked={fraudUnresolvedOnly} onChange={(e) => setFraudUnresolvedOnly(e.target.checked)} />
          {t('adminCredits.fraud.unresolvedOnly', 'Unresolved only')}
        </label>
        <ErrorNote error={flagError} />
        {fraud.loading ? (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.fraud.loading', 'Loading fraud flags…')}</p>
        ) : fraud.error ? (
          <ErrorNote error={fraud.error} />
        ) : fraud.entries.length === 0 ? (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{t('adminCredits.fraud.empty', 'No fraud flags.')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
            {fraud.entries.map((f, i) => (
              <div key={f.id ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                <span>
                  {fmtDate(f.createdAt)} · {f.reason || '—'}
                  <span style={{ ...chip, marginLeft: 8 }}>
                    {f.resolvedAt ? t('adminCredits.fraud.resolved', 'Resolved') : t('adminCredits.fraud.unresolved', 'Unresolved')}
                  </span>
                </span>
                {!f.resolvedAt && (
                  <button type="button" style={btnGhost(false)} onClick={() => setActionModal({ type: 'resolveFraud', flagId: f.id })}>
                    {t('adminCredits.actions.resolve', 'Resolve')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </DetailSection>

      {actionModal && (
        <CreditsActionModal
          type={typeof actionModal === 'string' ? actionModal : actionModal.type}
          organizationId={organizationId}
          mainCents={b?.mainCents}
          t={t}
          onClose={() => setActionModal(null)}
          onDone={async (fraudReason) => {
            if (typeof actionModal === 'object' && actionModal.type === 'resolveFraud') {
              setFlagError(null);
              try {
                await api.adminCredits.resolveFraudFlag(actionModal.flagId, fraudReason);
              } catch (e) {
                setFlagError(errMsg(e));
                return;
              }
            }
            setActionModal(null);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

function UserDetailModal({ userId, onClose }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const res = await api.adminUsers.detail(userId);
      setState({ loading: false, error: null, data: res });
    } catch (e) {
      setState({ loading: false, error: errMsg(e), data: null });
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setTab('overview'); }, [userId]);

  const d = state.data;
  const user = d?.user;
  const attribution = user?.attribution;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 20px', overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div style={{ ...card, width: '100%', maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        {state.loading ? (
          <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)' }}>Loading…</p>
        ) : state.error ? (
          <>
            <ErrorNote error={state.error} />
            <div style={{ marginTop: 14 }}>
              <button type="button" style={btnGhost(false)} onClick={onClose}>Close</button>
            </div>
          </>
        ) : !user ? (
          <>
            <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)' }}>User not found.</p>
            <div style={{ marginTop: 14 }}>
              <button type="button" style={btnGhost(false)} onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>
                  <AccountStatusDot status={user.status} />
                  {user.email}
                </h2>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>
                  {user.name || 'No name'} · {user.status}
                  {user.isAdmin ? ' · admin' : ''}
                </div>
              </div>
              <button type="button" style={btnGhost(false)} onClick={onClose}>Close</button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 14, borderBottom: '1px solid var(--border, #2a2a30)' }}>
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'credits', label: t('adminCredits.tabLabel', 'Credits') },
              ].map((tb) => (
                <button
                  key={tb.key}
                  type="button"
                  onClick={() => setTab(tb.key)}
                  style={{
                    background: 'transparent', border: 0, cursor: 'pointer', padding: '8px 4px',
                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    color: tab === tb.key ? 'var(--gold, #caa24a)' : 'var(--text-muted, #9a9aa2)',
                    borderBottom: tab === tb.key ? '2px solid var(--gold, #caa24a)' : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  {tb.label}
                </button>
              ))}
            </div>

            {d.warnings && d.warnings.length > 0 && (
              <AmberNote>Some data is unavailable: {warningsList(d.warnings)}</AmberNote>
            )}

            {tab === 'credits' ? (
              <CreditsTab organization={d.organization} />
            ) : (
              <>
            {/* Profile + attribution */}
            <DetailSection title="Profile & attribution">
              <DetailRow k="User ID" v={<code style={{ fontSize: 11.5 }}>{user.id}</code>} />
              <DetailRow k="Created" v={fmtDate(user.createdAt)} />
              <DetailRow k="Updated" v={fmtDate(user.updatedAt)} />
              <DetailRow k="Signup referrer" v={attribution?.signupReferrer || '—'} />
              <DetailRow k="UTM source" v={attribution?.utmSource || '—'} />
              <DetailRow k="UTM medium" v={attribution?.utmMedium || '—'} />
              <DetailRow k="UTM campaign" v={attribution?.utmCampaign || '—'} />
            </DetailSection>

            {/* Organization + billing */}
            <DetailSection title="Organization & billing">
              {d.organization ? (
                <>
                  <DetailRow k="Organization" v={d.organization.name} />
                  <DetailRow k="Plan" v={d.organization.plan || '—'} />
                  <DetailRow k="Billing status" v={d.organization.billingStatus || '—'} />
                  <DetailRow k="Has payment method" v={d.organization.hasPaymentMethod ? 'Yes' : 'No'} />
                  <DetailRow k="Billing gate exempt" v={d.organization.billingGateExempt ? 'Yes' : 'No'} />
                </>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No organization on this account.</p>
              )}
            </DetailSection>

            {/* Subscription */}
            <DetailSection title="Subscription">
              {d.subscription ? (
                <>
                  <DetailRow k="Status" v={<SubscriptionPill status={d.subscription.status} />} />
                  <DetailRow k="Plan" v={d.subscription.plan || '—'} />
                  <DetailRow k="Trial start" v={fmtDate(d.subscription.trialStart)} />
                  <DetailRow k="Trial end" v={fmtDate(d.subscription.trialEnd)} />
                  <DetailRow k="Current period end" v={fmtDate(d.subscription.currentPeriodEnd)} />
                  <DetailRow k="Cancels at period end" v={d.subscription.cancelAtPeriodEnd ? 'Yes' : 'No'} />
                </>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No subscription on record.</p>
              )}
            </DetailSection>

            {/* Social accounts */}
            <DetailSection title="Social accounts">
              {d.socialAccounts && d.socialAccounts.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {d.socialAccounts.map((sa, i) => (
                    <div key={`${sa.platform}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                      <span style={{ fontWeight: 600 }}>{sa.platform}</span>
                      <span style={{ display: 'flex', gap: 10, color: 'var(--text-muted, #9a9aa2)' }}>
                        <span style={chip}>{sa.status}</span>
                        <span>connected {fmtDate(sa.connectedAt)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No social accounts connected.</p>
              )}
            </DetailSection>

            {/* Usage */}
            <DetailSection title="Usage">
              {d.usage ? (
                <>
                  <DetailRow k="Generation jobs (30d)" v={d.usage.generationJobs30d} />
                  <DetailRow k="Published posts (30d)" v={d.usage.publishedPosts30d} />
                  {Object.entries(d.usage.counters || {}).map(([metric, c]) => (
                    <DetailRow key={metric} k={metric} v={`${c.used} / ${c.limit == null ? '∞' : c.limit}`} />
                  ))}
                </>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No usage data.</p>
              )}
            </DetailSection>

            {/* Payments */}
            <DetailSection title="Payments">
              {d.payments && d.payments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {d.payments.map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                      <span>{fmtDate(p.createdAt)} · {p.status || '—'}</span>
                      <span>
                        {fmtAmount(p.amount, p.currency)}
                        {p.invoiceUrl && (
                          <a href={p.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: 'var(--gold, #caa24a)' }}>invoice ↗</a>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No payments on record.</p>
              )}
            </DetailSection>

            {/* Autopilot */}
            <DetailSection title="Autopilot">
              {d.autopilot ? (
                <>
                  <DetailRow k="Enabled" v={d.autopilot.enabled ? 'Yes' : 'No'} />
                  <DetailRow k="Current phase" v={d.autopilot.currentPhase || '—'} />
                  <DetailRow k="Timezone" v={d.autopilot.timezone || '—'} />
                  <DetailRow k="Next run" v={fmtDate(d.autopilot.nextRunAt)} />
                  <DetailRow k="Last cycle started" v={fmtDate(d.autopilot.lastCycleStartedAt)} />
                  <DetailRow k="Last cycle completed" v={fmtDate(d.autopilot.lastCycleCompletedAt)} />
                </>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>No autopilot state.</p>
              )}
            </DetailSection>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── screen shell ───────────────────────────────────────────────────────────

export function AdminUsers() {
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [applied, setApplied] = useState(DEFAULT_DRAFT);
  const [offset, setOffset] = useState(0);

  const [listState, setListState] = useState({ loading: true, error: null, users: [], total: 0, limit: DEFAULT_LIMIT });
  const [statsState, setStatsState] = useState({ loading: true, error: null, data: null });
  const [selectedId, setSelectedId] = useState(null);

  const loadList = useCallback(async () => {
    setListState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminUsers.list({ ...applied, limit: DEFAULT_LIMIT, offset });
      setListState({
        loading: false, error: null,
        users: res.users || [], total: res.total ?? 0, limit: res.limit ?? DEFAULT_LIMIT,
      });
    } catch (e) {
      setListState((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [applied, offset]);

  const loadStats = useCallback(async () => {
    setStatsState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminUsers.stats();
      setStatsState({ loading: false, error: null, data: res.stats ? { ...res.stats, byPlan: res.byPlan, bySubscription: res.bySubscription, bySource: res.bySource, conversion: res.conversion, warnings: res.warnings } : null });
    } catch (e) {
      setStatsState({ loading: false, error: errMsg(e), data: null });
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const onApply = () => { setOffset(0); setApplied(draft); };
  const onReset = () => { setDraft(DEFAULT_DRAFT); setApplied(DEFAULT_DRAFT); setOffset(0); };

  const planOptions = useMemo(
    () => (statsState.data?.byPlan ? Object.keys(statsState.data.byPlan).filter(Boolean) : []),
    [statsState.data]
  );

  const total = listState.total;
  const limit = listState.limit || DEFAULT_LIMIT;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + limit, total);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
        <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>Users</h1>
        <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
          Filter, sort and drill into any account — subscription, billing, connected socials, usage, payments and autopilot in one place.
        </p>
      </div>

      <StatsStrip stats={statsState.data} loading={statsState.loading} error={statsState.error} />

      <FiltersPanel
        draft={draft}
        setDraft={setDraft}
        planOptions={planOptions}
        onApply={onApply}
        onReset={onReset}
        disabled={listState.loading}
      />

      <UsersTable users={listState.users} loading={listState.loading} error={listState.error} onSelect={setSelectedId} />

      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>
          <span>Showing {rangeStart}–{rangeEnd} of {total}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btnGhost(offset === 0 || listState.loading)} disabled={offset === 0 || listState.loading} onClick={() => setOffset((o) => Math.max(0, o - limit))}>
              Prev
            </button>
            <button type="button" style={btnGhost(offset + limit >= total || listState.loading)} disabled={offset + limit >= total || listState.loading} onClick={() => setOffset((o) => o + limit)}>
              Next
            </button>
          </div>
        </div>
      )}

      {selectedId && <UserDetailModal userId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

export default AdminUsers;
